/* eslint-disable react/no-array-index-key */
import { ClipboardEvent, FC, KeyboardEvent, useCallback, useEffect, useRef } from 'react';
import { useOtpInputStyles } from './OtpInput.styles';

const OTP_LENGTH = 6;

interface OtpInputProps {
  value: string[];
  onChange: (otp: string[]) => void;
  hasError?: boolean;
}

export const OtpInput: FC<OtpInputProps> = ({ value, onChange, hasError }) => {
  const cls = useOtpInputStyles();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = useCallback(
    (index: number, inputValue: string) => {
      if (inputValue.length > 1) {
        const digits = inputValue.replace(/\D/g, '').split('').slice(0, OTP_LENGTH);
        const newOtp = [...value];
        digits.forEach((digit, i) => {
          if (index + i < OTP_LENGTH) {
            newOtp[index + i] = digit;
          }
        });
        onChange(newOtp);
        const nextIndex = Math.min(index + digits.length, OTP_LENGTH - 1);
        inputRefs.current[nextIndex]?.focus();
        return;
      }

      if (inputValue && !/^\d$/.test(inputValue)) return;

      const newOtp = [...value];
      newOtp[index] = inputValue;
      onChange(newOtp);

      if (inputValue && index < OTP_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [onChange, value]
  );

  const handleKeyDown = useCallback(
    (index: number, e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !value[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
      if (e.key === 'ArrowLeft' && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
      if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [value]
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
      if (!pastedData) return;
      const newOtp = Array(OTP_LENGTH).fill('');
      pastedData.split('').forEach((char, i) => {
        newOtp[i] = char;
      });
      onChange(newOtp);
      const focusIndex = Math.min(pastedData.length, OTP_LENGTH - 1);
      inputRefs.current[focusIndex]?.focus();
    },
    [onChange]
  );

  return (
    <div className={cls.otpInputs} onPaste={handlePaste}>
      {value.map((digit, index) => (
        <div
          key={`otp-${index}`}
          className={`${cls.otpCell} ${!digit ? cls.otpCellEmpty : ''} ${
            hasError ? cls.otpCellError : ''
          }`}>
          <input
            ref={el => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            value={digit}
            onChange={e => handleChange(index, e.target.value)}
            onKeyDown={e => handleKeyDown(index, e)}
            className={cls.otpInput}
            aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
          />
        </div>
      ))}
    </div>
  );
};
